/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file type check file - based on sentry
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */
/**
 * Checks whether given value's type is one of a few Error or Error-like
 * {@link isError}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isError(wat) {
    switch (Object.prototype.toString.call(wat)) {
        case '[object Error]':
            return true;
        case '[object Exception]':
            return true;
        case '[object DOMException]':
            return true;
        default:
            return wat instanceof Error;
    }
}
/**
 * Checks whether given value's type is ErrorEvent
 * {@link isErrorEvent}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isErrorEvent(wat) {
    return Object.prototype.toString.call(wat) === '[object ErrorEvent]';
}
/**
 * Checks whether given value's type is DOMException
 * {@link isDOMException}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isDOMException(wat) {
    return Object.prototype.toString.call(wat) === '[object DOMException]';
}
/**
 * Checks whether given value's type is an undefined
 * {@link isUndefined}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isUndefined(wat) {
    return wat === void 0;
}
/**
 * Checks whether given value's type is a function
 * {@link isFunction}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isFunction(wat) {
    return typeof wat === 'function';
}
/**
 * Checks whether given value's type is a string
 * {@link isString}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isString(wat) {
    return Object.prototype.toString.call(wat) === '[object String]';
}
/**
 * Checks whether given value's type is a number
 * {@link isNumber}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isNumber(wat) {
    return Object.prototype.toString.call(wat) === '[object Number]';
}
/**
 * Checks whether given value's type is a bool
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isBoolean(wat) {
    return Object.prototype.toString.call(wat) === '[object Boolean]';
}
/**
 * Checks whether given value's is a primitive (undefined, null, number, boolean, string)
 * {@link isPrimitive}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isPrimitive(wat) {
    return wat === null || (typeof wat !== 'object' && typeof wat !== 'function');
}
/**
 * Checks whether given value's type is an array
 * {@link isArray}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isArray(wat) {
    return Object.prototype.toString.call(wat) === '[object Array]';
}
/**
 * Checks whether given value's type is an object literal
 * {@link isPlainObject}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isPlainObject(wat) {
    return Object.prototype.toString.call(wat) === '[object Object]';
}
/**
 * Checks whether given value's type is an regexp
 * {@link isRegExp}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isRegExp(wat) {
    return Object.prototype.toString.call(wat) === '[object RegExp]';
}
/**
 * Checks whether given value's type is a NaN
 * {@link isNaN}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isNaN(wat) {
    return wat !== wat;
}
/**
 * Checks whether given value's type is a SyntheticEvent
 * {@link isSyntheticEvent}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isSyntheticEvent(wat) {
    // tslint:disable-next-line:no-unsafe-any
    return (isPlainObject(wat) &&
        'nativeEvent' in wat &&
        'preventDefault' in wat &&
        'stopPropagation' in wat);
}
/**
 * Checks whether given value is an empty object
 * {@link isEmptyObject}.
 *
 * @param what A value to be checked.
 * @returns A boolean representing the result.
 */
export function isEmptyObject(what) {
    if (!isPlainObject(what)) {
        return false;
    }
    for (var item in what) {
        if (what.hasOwnProperty(item)) {
            return false;
        }
    }
    return true;
}
/**
 * Checks whether given value is an object
 * {@link isObject}.
 *
 * @param what A value to be checked.
 * @returns A boolean representing the result.
 */
export function isObject(what) {
    return typeof what === 'object' && what !== null;
}
//# sourceMappingURL=is.js.map